var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _this = this;
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect } from "react";
import { DeliveryType, DELIVERY_TYPE_STR_TO_ENUM, } from "@schema/types";
import { ModalViews, useUI } from "@context/ui";
import PickupPicker from "../PickupPicker";
import TimeEntryPicker from "../TimeEntryPicker";
import HavingTroubles from "./HavingTroubles";
import { Button, ErrorBox, Modal, Steps } from "@components/ui";
import { formatDate, formatPriceRange } from "@utils/index";
import { useDeliveryTypes } from "@hooks/api";
import { Check, Pencil } from "@components/icons";
import { ZipCodeForm, AddressForm } from "@components/form/AddressForm";
import { useStore } from "@context/store";
import { DELIVERY_TYPE_LABELS } from "@constants/index";
var Step = Steps.Step;
var Step1 = function () {
    var _a = useStore(), availableDeliveryTypes = _a.availableDeliveryTypes, type = _a.type, setDeliveryType = _a.setDeliveryType;
    var nextStep = useUI().nextStep;
    var handleClick = function (type) {
        setDeliveryType(type);
        setTimeout(nextStep);
    };
    return (_jsx("div", { children: availableDeliveryTypes.map(function (_a) {
            var minPrice = _a.minPrice, maxPrice = _a.maxPrice, deliveryType = __rest(_a, ["minPrice", "maxPrice"]);
            return (_jsx(Button, __assign({ className: "float-left mr-2 mb-2", active: deliveryType.type === type, onClick: handleClick.bind(_this, deliveryType.type) }, { children: DELIVERY_TYPE_LABELS[deliveryType.type] + " (" + formatPriceRange(minPrice, maxPrice) + ")" }), deliveryType.type));
        }) }, void 0));
};
var Step2 = function (_a) {
    var initialized = _a.initialized;
    var _b = useUI(), displayModal = _b.displayModal, closeModal = _b.closeModal, openModal = _b.openModal, setModalView = _b.setModalView;
    var _c = useStore(), type = _c.type, location = _c.location;
    var handleModalClose = function () {
        closeModal();
    };
    var handleSelectPickUp = function () {
        setModalView(ModalViews.PICKUP_PICKER);
        openModal();
    };
    return (_jsx("div", __assign({ className: "space-y-4 text-sm" }, { children: type === DeliveryType.Pickup ? (_jsxs(_Fragment, { children: [_jsx(Button, __assign({ onClick: handleSelectPickUp, primary: location !== null }, { children: "Select pickup" }), void 0),
                _jsx(HavingTroubles, {}, void 0),
                _jsx(Modal, __assign({ open: displayModal, onClose: handleModalClose }, { children: _jsx(PickupPicker, {}, void 0) }), void 0)] }, void 0)) : (_jsxs(_Fragment, { children: [initialized ? _jsx(AddressForm, {}, void 0) : _jsx(ZipCodeForm, {}, void 0),
                _jsx(HavingTroubles, {}, void 0)] }, void 0)) }), void 0));
};
var Step3 = function () {
    var location = useStore().location;
    if (location && "slots" in location) {
        return _jsx(TimeEntryPicker, { slots: location.slots }, void 0);
    }
    return _jsx(_Fragment, {}, void 0);
};
var STEPS = [
    {
        defaultTitle: "Select delivery method",
        Content: Step1,
    },
    {
        defaultTitle: "Select delivery location",
        Content: Step2,
    },
    {
        defaultTitle: "Select suitable time window",
        Content: Step3,
    },
];
var Form = function (_a) {
    var initialized = _a.initialized;
    var _b = useUI(), currentStep = _b.currentStep, setStep = _b.setStep;
    var _c = useStore(), timeEntry = _c.timeEntry, location = _c.location, type = _c.type, reset = _c.reset, setAvailableDeliveryTypes = _c.setAvailableDeliveryTypes;
    var _d = useDeliveryTypes(), deliveryTypes = _d.data, loading = _d.loading, error = _d.error;
    useEffect(function () {
        if (deliveryTypes) {
            var availableDeliveryTypes = [];
            for (var _i = 0, deliveryTypes_1 = deliveryTypes; _i < deliveryTypes_1.length; _i++) {
                var _a = deliveryTypes_1[_i];
                var available = _a.available, minPrice = _a.minPrice, maxPrice = _a.maxPrice, rest = __rest(_a, ["available", "minPrice", "maxPrice"]);
                if (available) {
                    availableDeliveryTypes.push({
                        type: DELIVERY_TYPE_STR_TO_ENUM[rest.type],
                        minPrice: minPrice,
                        maxPrice: maxPrice,
                    });
                }
            }
            setAvailableDeliveryTypes(availableDeliveryTypes);
        }
    }, [deliveryTypes]);
    var getFormattedDate = function () {
        var _a = timeEntry, startTime = _a.startTime, endTime = _a.endTime, date = _a.date;
        if (!date) {
            return "(" + startTime + "-" + endTime + ")";
        }
        return formatDate(date) + " (" + startTime + "-" + endTime + ")";
    };
    var getDefaultTitle = function () {
        if (type === DeliveryType.Pickup) {
            var _a = location, name_1 = _a.name, _b = _a.address, address1 = _b.address1, city = _b.city, country = _b.country;
            return (_jsxs(_Fragment, { children: [_jsx("strong", { children: "Pickup - " + name_1 + " - " + address1 + ", " + city + ", " + country }, void 0),
                    _jsx("p", __assign({ className: "text-sm" }, { children: getFormattedDate() }), void 0)] }, void 0));
        }
        var zipCode = location.zipCode;
        return (_jsxs(_Fragment, { children: [_jsx("strong", { children: "Postal code - " + zipCode }, void 0),
                _jsx("p", __assign({ className: "text-sm" }, { children: getFormattedDate() }), void 0)] }, void 0));
    };
    var getTitle = function (step, defaultTitle) {
        var isPickup = type === DeliveryType.Pickup;
        var stepsFinished = disabledFrom();
        switch (step) {
            case 0:
                if (stepsFinished > 0 && type) {
                    return DELIVERY_TYPE_LABELS[type];
                }
                break;
            case 1:
                if (stepsFinished > 1) {
                    if (isPickup) {
                        /* const { address } = location; */
                        var _a = location, name_2 = _a.name, _b = _a.address, address1 = _b.address1, city = _b.city, country = _b.country;
                        return name_2 + " - " + address1 + ", " + city + ", " + country;
                    }
                    var zipCode = location.zipCode;
                    return "Postal code - " + zipCode;
                }
                if (isPickup) {
                    return "Select pickup location";
                }
                return "Please enter your postcode";
            case 2:
                if (stepsFinished > 2) {
                    return getFormattedDate();
                }
        }
        return defaultTitle;
    };
    var handleEdit = function () {
        if (initialized) {
            reset();
            setStep(0);
        }
        else {
            setStep(2);
        }
    };
    var disabledFrom = useCallback(function () {
        if (timeEntry) {
            return 3;
        }
        if (location) {
            return 2;
        }
        if (type !== null) {
            return 1;
        }
        return 0;
    }, [timeEntry, location, type]);
    if (error) {
        return _jsx(ErrorBox, {}, void 0);
    }
    if (type !== null && location && timeEntry && currentStep > 2) {
        return (_jsxs("div", __assign({ className: "relative my-5 flex items-center" }, { children: [_jsx("div", __assign({ className: "mr-5 flex-shrink-0 w-8 h-8 border border-black items-center flex justify-center rounded-full" }, { children: _jsx(Check, {}, void 0) }), void 0),
                _jsxs("div", { children: [getDefaultTitle(), _jsx(Button, __assign({ className: "absolute bg-primary right-2 top-0 rounded-full p-2", onClick: handleEdit }, { children: _jsx(Pencil, { className: "w-4 h-4" }, void 0) }), void 0)] }, void 0)] }), void 0));
    }
    return (_jsx(_Fragment, { children: loading ? (_jsxs("div", __assign({ className: "w-full h-full py-5 space-y-4 opacity-40" }, { children: [_jsxs("div", __assign({ className: "animate-pulse flex space-x-6" }, { children: [_jsx("div", { className: "rounded-full bg-decent h-8 w-8" }, void 0),
                        _jsxs("div", __assign({ className: "flex-1 space-y-4 py-2" }, { children: [_jsx("div", { className: "h-4 bg-decent rounded w-40" }, void 0),
                                _jsx("div", { className: "h-8 bg-decent rounded w-60" }, void 0)] }), void 0)] }), void 0),
                _jsxs("div", __assign({ className: "animate-pulse flex space-x-6" }, { children: [_jsx("div", { className: "rounded-full bg-decent h-8 w-8" }, void 0),
                        _jsx("div", __assign({ className: "flex-1 space-y-4 py-2" }, { children: _jsx("div", { className: "h-4 bg-decent rounded w-40" }, void 0) }), void 0)] }), void 0),
                _jsxs("div", __assign({ className: "animate-pulse flex space-x-6" }, { children: [_jsx("div", { className: "rounded-full bg-decent h-8 w-8" }, void 0),
                        _jsx("div", __assign({ className: "flex-1 space-y-4 py-2" }, { children: _jsx("div", { className: "h-4 bg-decent rounded w-40" }, void 0) }), void 0)] }), void 0)] }), void 0)) : (_jsx(Steps, __assign({ className: "my-5", current: currentStep, direction: "vertical", onChange: function (index) {
                if (disabledFrom() < index) {
                    return;
                }
                setStep(index);
            } }, { children: STEPS.map(function (_a, index) {
                var defaultTitle = _a.defaultTitle, Content = _a.Content;
                var disabled = disabledFrom() < index;
                return (_jsx(Step, { title: getTitle(index, defaultTitle), disabled: disabled, description: currentStep === index && (_jsx(Content, { initialized: initialized }, void 0)) }, "step-" + index));
            }) }), void 0)) }, void 0));
};
export default Form;
